import { z } from "zod";
import { type FilePurpose, filePurposeSchema, fileStatusSchema } from "./enums";

/**
 * Upload rules shared by the API (which issues upload tokens) and the storage
 * service (which enforces them). See docs/architecture/storage-service.md.
 */

/**
 * HEIC is not accepted: the prebuilt sharp cannot decode HEVC. iPhones send
 * JPEG when a photo is picked in the browser, and Android phones take JPEG.
 */
export const IMAGE_MIMES = ["image/jpeg", "image/png", "image/webp"] as const;
export const UPLOAD_MIMES = [...IMAGE_MIMES, "application/pdf"] as const;
export const uploadMimeSchema = z.enum(UPLOAD_MIMES);
export type UploadMime = z.infer<typeof uploadMimeSchema>;

const MB = 1024 * 1024;

export type FileRule = {
	mimes: readonly UploadMime[];
	maxBytes: number;
	/** Longest side after processing, or null to store the file as it is. */
	maxSide: number | null;
	/**
	 * Served on the CDN host. Private files are read by the worker, or
	 * downloaded with a short-lived signed link from the API.
	 */
	public: boolean;
	/** Whether clients may ask for an upload token. Generated files are stored by the API only. */
	uploadable: boolean;
};

export const PDF_MIMES = ["application/pdf"] as const;

export const FILE_RULES: Readonly<Record<FilePurpose, FileRule>> = {
	item_image: {
		mimes: IMAGE_MIMES,
		maxBytes: 10 * MB,
		maxSide: 2000,
		public: true,
		uploadable: true,
	},
	category_image: {
		mimes: IMAGE_MIMES,
		maxBytes: 10 * MB,
		maxSide: 2000,
		public: true,
		uploadable: true,
	},
	logo: { mimes: IMAGE_MIMES, maxBytes: 5 * MB, maxSide: 2000, public: true, uploadable: true },
	cover: { mimes: IMAGE_MIMES, maxBytes: 10 * MB, maxSide: 2560, public: true, uploadable: true },
	gallery: { mimes: IMAGE_MIMES, maxBytes: 10 * MB, maxSide: 2560, public: true, uploadable: true },
	offer_banner: {
		mimes: IMAGE_MIMES,
		maxBytes: 10 * MB,
		maxSide: 2560,
		public: true,
		uploadable: true,
	},
	site_asset: {
		mimes: IMAGE_MIMES,
		maxBytes: 10 * MB,
		maxSide: 2560,
		public: true,
		uploadable: true,
	},
	menu_source: {
		mimes: UPLOAD_MIMES,
		maxBytes: 25 * MB,
		maxSide: null,
		public: false,
		uploadable: true,
	},
	/** Platform invoices, made by the API from the invoice snapshot (P10-T08). */
	invoice_pdf: {
		mimes: PDF_MIMES,
		maxBytes: 2 * MB,
		maxSide: null,
		public: false,
		uploadable: false,
	},
};

export { IMAGE_WIDTHS, MAX_IMAGE_WIDTH, variantWidth } from "./image-widths";

/** Default quotas per restaurant until plans set them (Phase 10). */
export const DEFAULT_FILE_QUOTA = { files: 2000, bytes: 2 * 1024 * MB } as const;

/** Claims of an upload token, signed by the API and checked by the storage service. */
export const uploadTokenClaimsSchema = z.object({
	v: z.literal(1),
	/** File ID: the `pending` row in `storage.files`. */
	fid: z.uuid(),
	/** Restaurant, or null for platform files. */
	rid: z.uuid().nullable(),
	pur: filePurposeSchema,
	/** Maximum size in bytes. */
	max: z.number().int().positive(),
	/** Allowed types, detected from content. */
	mime: z.array(uploadMimeSchema).min(1),
	/** Expiry, seconds since the epoch. */
	exp: z.number().int(),
	uid: z.uuid(),
});
export type UploadTokenClaims = z.infer<typeof uploadTokenClaimsSchema>;

/**
 * Claims of a download link for a private file, signed by the API and checked
 * by the storage service (`GET /d/<token>`). `k` keeps an upload token from
 * passing as one.
 */
export const downloadTokenClaimsSchema = z.object({
	v: z.literal(1),
	k: z.literal("download"),
	fid: z.uuid(),
	/** The file name the browser saves: letters, digits, `.`, `-` and `_`. */
	name: z.string().regex(/^[A-Za-z0-9._-]{1,100}$/),
	/** Expiry, seconds since the epoch. */
	exp: z.number().int(),
});
export type DownloadTokenClaims = z.infer<typeof downloadTokenClaimsSchema>;

/** A short-lived link to a private file, such as an invoice PDF. */
export const downloadLinkSchema = z.object({
	url: z.url(),
	expiresAt: z.iso.datetime(),
});
export type DownloadLink = z.infer<typeof downloadLinkSchema>;

export const createUploadTokenInputSchema = z.object({
	purpose: filePurposeSchema,
	/** The type the client believes it has. The storage service checks the bytes. */
	mime: uploadMimeSchema,
	size: z.number().int().positive(),
});
export type CreateUploadTokenInput = z.infer<typeof createUploadTokenInputSchema>;

export const uploadTicketSchema = z.object({
	fileId: z.uuid(),
	/** POST the bytes here with the token. */
	uploadUrl: z.url(),
	token: z.string(),
	expiresAt: z.iso.datetime(),
});
export type UploadTicket = z.infer<typeof uploadTicketSchema>;

/** A stored file as clients see it. Build URLs with the image URL helper. */
export const fileInfoSchema = z.object({
	id: z.uuid(),
	purpose: filePurposeSchema,
	status: fileStatusSchema,
	mime: z.string(),
	sizeBytes: z.number().int(),
	width: z.number().int().nullable(),
	height: z.number().int().nullable(),
	blurhash: z.string().nullable(),
});
export type FileInfo = z.infer<typeof fileInfoSchema>;
